import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { RecurringItem, CreateRecurringItemRequest, UpdateRecurringItemRequest } from '../types/api';
import { recurringItemService } from '../services/api';

interface CachedRecurringItem {
  data: RecurringItem;
  fetchedAt: number;
}

type CacheEvent = 
  | { type: 'update'; id: string }
  | { type: 'create'; id: string }
  | { type: 'delete'; id: string }
  | { type: 'invalidate'; id: string }
  | { type: 'invalidate-all' };

interface RecurringItemsContextValue {
  getRecurringItem: (id: string) => RecurringItem | null;
  getRecurringItemAsync: (id: string) => Promise<RecurringItem | null>;
  updateRecurringItem: (id: string, data: UpdateRecurringItemRequest) => Promise<RecurringItem>;
  createRecurringItem: (data: CreateRecurringItemRequest) => Promise<RecurringItem>;
  deleteRecurringItem: (id: string) => Promise<void>;
  invalidateCache: (id?: string) => void;
  subscribe: (listener: (event: CacheEvent) => void) => () => void;
  isLoading: boolean;
}

const RecurringItemsContext = createContext<RecurringItemsContextValue | null>(null);

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes before considering stale
const AGGRESSIVE_REFRESH = 1 * 60 * 1000; // 1 minute before background refresh

export const RecurringItemsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Record<string, CachedRecurringItem>>({});
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const eventListeners = useRef<Set<(event: CacheEvent) => void>>(new Set());

  // Event system for reactive invalidation
  const emitEvent = useCallback((event: CacheEvent) => {
    eventListeners.current.forEach(listener => listener(event));
  }, []);

  const subscribe = useCallback((listener: (event: CacheEvent) => void) => {
    eventListeners.current.add(listener);
    return () => eventListeners.current.delete(listener);
  }, []);

  // Invalidation helper
  const invalidateCache = useCallback((id?: string) => {
    if (id) {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[id];
        return newCache;
      });
      emitEvent({ type: 'invalidate', id });
    } else {
      setCache({});
      emitEvent({ type: 'invalidate-all' });
    }
  }, [emitEvent]);

  // Background refresh without blocking
  const refreshInBackground = useCallback((id: string) => {
    if (loading.has(id)) return; // Already fetching

    setLoading(prev => new Set(prev).add(id));
    recurringItemService.getRecurringItem(id)
      .then(response => {
        if (response.success && response.data) {
          const data = response.data;
          setCache(prev => ({
            ...prev,
            [id]: { data, fetchedAt: Date.now() }
          }));
        }
      })
      .catch(error => {
        // If item was deleted (404), remove it from cache to prevent repeated 404s
        if (error.response?.status === 404) {
          console.log(`Recurring item ${id} no longer exists, removing from cache`);
          setCache(prev => {
            const newCache = { ...prev };
            delete newCache[id];
            return newCache;
          });
        } else {
          console.error('Background refresh failed:', error);
        }
      })
      .finally(() => {
        setLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      });
  }, [loading]);

  // Fetch fresh data
  const fetchFresh = useCallback(async (id: string): Promise<RecurringItem | null> => {
    setLoading(prev => new Set(prev).add(id));
    try {
      const response = await recurringItemService.getRecurringItem(id);
      if (response.success && response.data) {
        const data = response.data;
        setCache(prev => ({
          ...prev,
          [id]: { data, fetchedAt: Date.now() }
        }));
        return data;
      }
      return null;
    } catch (error: any) {
      // If item was deleted (404), remove it from cache to prevent repeated 404s
      if (error.response?.status === 404) {
        console.log(`Recurring item ${id} no longer exists, removing from cache`);
        setCache(prev => {
          const newCache = { ...prev };
          delete newCache[id];
          return newCache;
        });
      }
      return null;
    } finally {
      setLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, []);

  // Wait for ongoing fetch to complete
  const waitForFetch = useCallback((id: string): Promise<RecurringItem | null> => {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!loading.has(id)) {
          clearInterval(checkInterval);
          resolve(cache[id]?.data || null);
        }
      }, 50);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(null);
      }, 10000);
    });
  }, [loading, cache]);

  // Get cached item synchronously (returns null if not cached)
  const getRecurringItem = useCallback((id: string): RecurringItem | null => {
    return cache[id]?.data || null;
  }, [cache]);

  // Get item with async fetch if needed
  const getRecurringItemAsync = useCallback(async (id: string): Promise<RecurringItem | null> => {
    const cached = cache[id];
    const now = Date.now();

    if (cached) {
      const age = now - cached.fetchedAt;

      // Strategy 1: If fresh enough, return immediately
      if (age < AGGRESSIVE_REFRESH) {
        return cached.data;
      }

      // Strategy 2: If stale but not expired, return cached + refresh in background
      if (age < CACHE_TTL) {
        // Background refresh (fire and forget)
        refreshInBackground(id);
        return cached.data;
      }

      // Too old, fall through to fetch fresh
    }

    // Avoid duplicate fetches
    if (loading.has(id)) {
      return waitForFetch(id);
    }

    // Fetch fresh data
    return fetchFresh(id);
  }, [cache, loading, refreshInBackground, waitForFetch, fetchFresh]);

  // Update recurring item with optimistic updates
  const updateRecurringItem = useCallback(async (
    id: string, 
    data: UpdateRecurringItemRequest
  ): Promise<RecurringItem> => {
    // Save old cache for rollback
    const oldCache = cache[id];
    
    // Optimistically update cache
    if (oldCache) {
      setCache(prev => ({
        ...prev,
        [id]: {
          data: { ...oldCache.data, ...data } as RecurringItem,
          fetchedAt: Date.now()
        }
      }));
    }

    try {
      const response = await recurringItemService.updateRecurringItem(id, data);
      if (response.success && response.data) {
        // Replace with server truth
        const updatedData = response.data;
        setCache(prev => ({
          ...prev,
          [id]: { data: updatedData, fetchedAt: Date.now() }
        }));
        emitEvent({ type: 'update', id });
        return updatedData;
      }
      throw new Error(response.error || 'Failed to update recurring item');
    } catch (error) {
      // Rollback on error
      if (oldCache) {
        setCache(prev => ({ ...prev, [id]: oldCache }));
      } else {
        invalidateCache(id);
      }
      throw error;
    }
  }, [cache, emitEvent, invalidateCache]);

  // Create new recurring item
  const createRecurringItem = useCallback(async (
    data: CreateRecurringItemRequest
  ): Promise<RecurringItem> => {
    const response = await recurringItemService.createRecurringItem(data);
    if (response.success && response.data) {
      const newItem = response.data;
      setCache(prev => ({
        ...prev,
        [newItem.id]: { data: newItem, fetchedAt: Date.now() }
      }));
      emitEvent({ type: 'create', id: newItem.id });
      return newItem;
    }
    throw new Error(response.error || 'Failed to create recurring item');
  }, [emitEvent]);

  // DEPRECATED: Delete recurring item (not supported - use convert-to-non-recurring instead)
  const deleteRecurringItem = useCallback(async (id: string): Promise<void> => {
    console.warn('[DEPRECATED] deleteRecurringItem should not be used. Use convert-to-non-recurring instead.');
    throw new Error('Deleting recurring items is not supported. Please use "Convert to Non-Recurring" to keep one instance.');
  }, []);

  // Strategy 4: Global refresh on window focus (catch external changes)
  useEffect(() => {
    const handleFocus = () => {
      const now = Date.now();
      Object.keys(cache).forEach(id => {
        const age = now - cache[id].fetchedAt;
        if (age > AGGRESSIVE_REFRESH) {
          refreshInBackground(id);
        }
      });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [cache, refreshInBackground]);

  const value: RecurringItemsContextValue = {
    getRecurringItem,
    getRecurringItemAsync,
    updateRecurringItem,
    createRecurringItem,
    deleteRecurringItem,
    invalidateCache,
    subscribe,
    isLoading: loading.size > 0
  };

  return (
    <RecurringItemsContext.Provider value={value}>
      {children}
    </RecurringItemsContext.Provider>
  );
};

// Custom hook for consuming the context
export const useRecurringItems = (): RecurringItemsContextValue => {
  const context = useContext(RecurringItemsContext);
  if (!context) {
    throw new Error('useRecurringItems must be used within RecurringItemsProvider');
  }
  return context;
};
